import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { D_STEPS, FIVE_WHY_STEP, progress8d, maturity } from "../../lib/eightd";
import { Save, CalendarClock } from "lucide-react";

// Storage-agnostic 8D report renderer shared by customer + supplier complaints.
// value: { [stepKey]: { content, done, owner_id, owner_name, due, five_why } }
export function EightDReport({ value = {}, onChangeStep, onSaveStep, disabled, owners = [], steps = D_STEPS, savingKey }) {
  const prog = progress8d(value, steps);
  const mat = maturity(value, steps);
  const ownerName = (id) => owners.find((o) => o.id === id)?.name || "";

  return (
    <div data-testid="eightd-report">
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-[#1B2A4A]">8D-Report</h3>
          <Badge className={`${mat.cls} hover:${mat.cls}`} data-testid="eightd-maturity">{mat.label}</Badge>
        </div>
        <div className="flex items-center gap-2 min-w-[160px]">
          <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full bg-[#F5A623] transition-all duration-300" style={{ width: `${prog.pct}%` }} data-testid="eightd-progress-bar" />
          </div>
          <span className="text-[11px] font-mono text-gray-500 whitespace-nowrap" data-testid="eightd-progress-text">{prog.done}/{prog.total}</span>
        </div>
      </div>

      <div className="space-y-2.5">
        {steps.map((s) => {
          const v = value[s.key] || {};
          const isFiveWhy = s.key === FIVE_WHY_STEP;
          return (
            <div key={s.key} className="border border-gray-100 rounded-lg p-3" data-testid={`eightd-step-${s.key}`}>
              <div className="flex items-start gap-2.5">
                <span className={`mt-0.5 h-6 w-6 shrink-0 rounded-full text-[10px] font-bold flex items-center justify-center uppercase ${v.done ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-400"}`}>
                  {s.key}
                </span>
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-xs font-semibold text-gray-600">{s.label}</Label>
                    <label className="flex items-center gap-1.5 text-[11px] text-gray-500 whitespace-nowrap">
                      <Checkbox data-testid={`eightd-done-${s.key}`} checked={!!v.done} disabled={disabled}
                        onCheckedChange={(c) => onChangeStep(s.key, { done: !!c })} /> erledigt
                    </label>
                  </div>
                  <Textarea data-testid={`eightd-content-${s.key}`} rows={2} disabled={disabled}
                    value={v.content || ""} onChange={(e) => onChangeStep(s.key, { content: e.target.value })} />
                  {isFiveWhy && (
                    <div className="pl-1">
                      <Label className="text-[11px] text-gray-500">5-Why (Warum × 5)</Label>
                      <Textarea data-testid="eightd-five-why" rows={2} disabled={disabled} className="mt-1"
                        placeholder="Warum? … Warum? … (5 Ebenen)"
                        value={v.five_why || ""} onChange={(e) => onChangeStep(s.key, { five_why: e.target.value })} />
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <select data-testid={`eightd-owner-${s.key}`} disabled={disabled}
                      className="h-8 text-xs border border-gray-200 rounded-md px-2 bg-white text-gray-600 disabled:opacity-60"
                      value={v.owner_id || ""}
                      onChange={(e) => onChangeStep(s.key, { owner_id: e.target.value || null, owner_name: ownerName(e.target.value) || null })}>
                      <option value="">Verantwortlich…</option>
                      {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                    <div className="flex items-center gap-1 text-gray-400">
                      <CalendarClock className="h-3.5 w-3.5" />
                      <input type="date" data-testid={`eightd-due-${s.key}`} disabled={disabled}
                        className="h-8 text-xs border border-gray-200 rounded-md px-2 bg-white text-gray-600 disabled:opacity-60"
                        value={(v.due || "").slice(0, 10)} onChange={(e) => onChangeStep(s.key, { due: e.target.value || null })} />
                    </div>
                    {onSaveStep && !disabled && (
                      <Button size="sm" variant="ghost" className="h-8 text-xs ml-auto" data-testid={`eightd-save-${s.key}`}
                        disabled={savingKey === s.key} onClick={() => onSaveStep(s.key)}>
                        <Save className="h-3 w-3 mr-1" />Speichern
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
